import * as React from "react";
import { Stack, TextField, AlertColor } from "@mui/material";
import CclDrawer from "../../../components/common/cclDrawer/cclDrawer";
import CclDrawerActionBar from "../../../components/common/cclDrawer/cclDrawerActionBar";
import CclButton from "../../../components/common/cclButtons/cclButton";
import CclLoadingButton from "../../../components/common/cclButtons/cclLoadingButton";
import { useAddGlGroupMutation, useModifyGlGroupMutation } from "../../../services/cclTokenedPayrollApi";
import { GlGroup } from "../../../services/types/payrollEventTypes";
import useLogAccessEvent from "../../../hooks/useLogAccessEvent";

export interface AddGlGroupDrawerProps {
    open: boolean;
    mode?: "add" | "edit";
    group?: GlGroup | null;
    onClose: () => void;
    onSaveResult: (message: string, severity: AlertColor) => void;
}

const AddGlGroupDrawer: React.FC<AddGlGroupDrawerProps> = (props) => {
    const isEditMode = props.mode === "edit";
    const [glCode, setGlCode] = React.useState<string>("");
    const [glDescription, setGlDescription] = React.useState<string>("");
    const [addGlGroup, { isLoading }] = useAddGlGroupMutation();
    const [modifyGlGroup, { isLoading: isModifyLoading }] = useModifyGlGroupMutation();
    const { logEvent } = useLogAccessEvent();

    React.useEffect(() => {
        if (!props.open) {
            return;
        }

        if (isEditMode && props.group) {
            setGlCode(props.group.groupCode ?? "");
            setGlDescription(props.group.groupDesc ?? "");
            return;
        }

        setGlCode("");
        setGlDescription("");
    }, [props.open, isEditMode, props.group]);

    const isSaving = isLoading || isModifyLoading;

    const canSave = glCode.trim() !== "" && glDescription.trim() !== "" && !isSaving;

    const handleSave = () => {
        const payload = {
            groupCode: glCode.trim(),
            groupDesc: glDescription.trim(),
        };

        const saveAction = isEditMode ? modifyGlGroup(payload) : addGlGroup(payload);

        saveAction
            .unwrap()
            .then(() => {
                logEvent(isEditMode ? "GLGroupEdited" : "GLGroupAdded", null);
                props.onSaveResult(isEditMode ? "GL Group Edited" : "GL Group Added", "success");
                props.onClose();
            })
            .catch((error) => {
                console.error("Error saving GL group:", error);
                props.onSaveResult(
                    isEditMode ? "Failed to update GL Group" : "Failed to add GL Group",
                    "error"
                );
            });
    };

    return (
        <CclDrawer title={isEditMode ? "Edit GL Group" : "Add GL Group"} open={props.open} onClose={props.onClose}>
            <Stack spacing={2} sx={{ pt: 1 }}>
                <TextField
                    id="add-gl-group-code"
                    label="Code"
                    size="small"
                    fullWidth
                    value={glCode}
                    disabled={isEditMode}
                    onChange={(e) => setGlCode(e.target.value)}
                />
                <TextField
                    id="add-gl-group-description"
                    label="Description"
                    size="small"
                    fullWidth
                    value={glDescription}
                    onChange={(e) => setGlDescription(e.target.value)}
                />
                <CclDrawerActionBar>
                    <CclLoadingButton mode="primary" loading={isSaving} disabled={!canSave} onClick={handleSave}>
                        Save
                    </CclLoadingButton>
                    <CclButton mode="secondary" onClick={props.onClose}>
                        Close
                    </CclButton>
                </CclDrawerActionBar>
            </Stack>
        </CclDrawer>
    );
};

export default AddGlGroupDrawer;